import React from "react";
import type { ModalViewType } from "../../../../../../../common-deprecated/types/CommonTypes";
import type { OrderCheckoutModalType } from "../../../../utils/modalConstants";
import Text from "../../../../../shared-components/css-modules/toyota/atoms/Text";
import { useCarConfigSelector } from "../../../../redux/store";
import {
    getEcoBadgeType,
    hasAlternativeEcoSpecFlow,
} from "../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import { getEcoBadgeUrl } from "../../../../../../../common-deprecated/utils";
import { getEcoTagType } from "../../../../../../shared-logic/utils/typedUtils";
import { selectMotorization } from "../../../../redux/slices/CarConfigMotorizationSlice";
import EfficiencyClass from "../../../../../shared-components/eco/EfficiencyClass";
import { useCarIds, useCarConfigCCISNormalization } from "../../../../hooks/useAphCCISNormalization";
import { getCarImage } from "../../../../../shared-logic/utils/imageUtils";
import styles from "./OrderCheckoutModal.module.scss";
import SidePanel from "../../../../../shared-components/css-modules/toyota/organisms/SidePanel";
import Container from "../../../../../../../common-deprecated/styles/toyota/Container";
import Icon from "../../../../../shared-components/css-modules/toyota/atoms/Icon";
import type { IconVariantTypes } from "../../../../../shared-components/css-modules/toyota/atoms/Icon.types";
import CtaButton from "../cta/CtaButton";
import type { CtaButtonType } from "../../../../utils/types/AceConfigType";
import { useCarConfigLabel } from "../../../../utils/labelUtils";
import Price from "../price/Price";
import { getMotorizationLabelConfig } from "../../../../utils/motorizationUtils";

const useVisual = (): string | undefined => {
    const carIds = useCarIds();
    const carImageInput = useCarConfigCCISNormalization();
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);
    const carImage = getCarImage(
        commonSettings,
        3,
        carIds,
        { type: "jpg", width: 1000, height: 450 },
        carImageInput,
    ).src;
    return carImage;
};

const OrderCheckoutModal = (props: ModalViewType<OrderCheckoutModalType>): JSX.Element | null => {
    const { show, close, modalSettings } = props;
    const { checkoutType } = modalSettings;

    const config = useCarConfigSelector((state) => state.carConfigSettings.checkoutModalConfigs?.[checkoutType]);
    const financeOption = useCarConfigSelector((state) => state.utils.financeOption);
    const basket = useCarConfigSelector((state) => state.basket);
    const [paymentMethodLabel, totalLabel, buyTitle, leaseTitle, reserveTitle, financeOptionLabel] = useCarConfigLabel([
        "carConfigPaymentMethod",
        financeOption === "cash" ? "carConfigCashTotal" : "carConfigMontlyTotal",
        financeOption === "cash" ? "carConfigBuyCash" : "carConfigBuyMonthly",
        "carConfigLease",
        "carConfigReserve",
        financeOption === "cash" ? "carConfigCashPurchase" : "carConfigMonthlyPurchase",
    ]);
    const title = checkoutType === "buy" ? buyTitle : checkoutType === "lease" ? leaseTitle : reserveTitle;
    const cta: CtaButtonType = {
        title,
        isPrimary: true,
        type: `go-to-${checkoutType}`,
    };

    const car = useCarConfigSelector((state) => state.car);
    const commonSettings = useCarConfigSelector((state) => state.commonSettings);

    const ecoTag = getEcoTagType(car.motorization.engine);
    const ecoBadge = getEcoBadgeType(commonSettings, ecoTag, car.model.id);
    const ecoBadgeUrl = getEcoBadgeUrl(ecoBadge, commonSettings);
    const alternativeEcoSpecFlow = hasAlternativeEcoSpecFlow(commonSettings);

    const imgVisual = useVisual();
    const motorizations = useCarConfigSelector(selectMotorization);
    const currentMotorization = motorizations.find(({ carId }) => carId === car.id);
    if (!currentMotorization) return null;
    const efficiencyLabelConfig = getMotorizationLabelConfig(currentMotorization, alternativeEcoSpecFlow);

    return (
        <SidePanel
            open={show}
            onRequestClose={close}
            variant="fullscreen"
            fromDirection="block-end"
            className={styles["split-color"]}
            title={config?.title}
        >
            <Container className={styles.container}>
                <div className={styles.grid}>
                    <div>
                        {config && (
                            <div className={styles.panel}>
                                <Text tag="h2" variant="apheleia-h1" spacing="spacing-3" className={styles.heading}>
                                    {config.title}
                                </Text>
                                {config.description && (
                                    <Text
                                        tag="div"
                                        variant="apheleia-h5"
                                        dangerouslySetInnerHTML={{ __html: config.description }}
                                    />
                                )}
                                {config.properties.length && (
                                    <ul role="list" className={styles["usp-list"]}>
                                        {config.properties.map((property) => (
                                            <li key={property.title} className={styles["usp-list-item"]}>
                                                {property.icon && (
                                                    <Icon
                                                        variant={property.icon as IconVariantTypes}
                                                        aria-hidden="true"
                                                        className={styles.icon}
                                                    />
                                                )}
                                                <div>
                                                    <Text tag="h3" variant="apheleia-h4">
                                                        {property.title}
                                                    </Text>
                                                    {property.subtitle && (
                                                        <Text tag="div" variant="apheleia-body" color="grey-7">
                                                            {property.subtitle}
                                                        </Text>
                                                    )}
                                                </div>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                    <div>
                        <div className={styles.panel}>
                            <img src={imgVisual} alt="" className={styles.image} />
                            <div className={styles["car-info"]}>
                                <Text tag="h2" variant="apheleia-h2" className={styles["car-title"]}>
                                    {car.model.webtitleSectionName} <span>{car.grade.webtitleSectionName}</span>
                                </Text>
                                <div>
                                    <Text tag="div" variant="apheleia-h4">
                                        {car.body.webtitleSectionName}
                                    </Text>
                                    {efficiencyLabelConfig.show && (
                                        <EfficiencyClass label={efficiencyLabelConfig.label} />
                                    )}
                                    {ecoBadgeUrl && (
                                        <img
                                            src={ecoBadgeUrl}
                                            style={{ width: 24, height: 24, display: "inline-block" }}
                                            alt=""
                                        />
                                    )}
                                    <Text variant="apheleia-hybrid-label">{car.motorization.engine.type}</Text>
                                </div>
                                <Text variant="apheleia-h5" spacing="spacing-0" color="grey-7">
                                    {car.motorization.wheeldrive.name}
                                    <br />
                                    {car.motorization.engine.name}, {car.motorization.transmission.name}
                                </Text>
                            </div>
                            {checkoutType !== "reserve" && (
                                <>
                                    <p className={styles["car-payment"]}>
                                        <Text tag="span" variant="apheleia-h4">
                                            {paymentMethodLabel}
                                        </Text>
                                        <Text tag="span" variant="apheleia-h4">
                                            {financeOptionLabel}
                                        </Text>
                                    </p>
                                    <div className={styles["car-total"]}>
                                        <Text tag="h4" variant="apheleia-h3">
                                            {totalLabel}
                                        </Text>
                                        <Text tag="h4" variant="apheleia-h3">
                                            <Price price={basket.total} financeItemId="total" />
                                        </Text>
                                    </div>
                                </>
                            )}
                            <CtaButton ctaConfig={cta} sizing="full-width" useOrderCtaTracking />
                        </div>
                    </div>
                </div>
            </Container>
        </SidePanel>
    );
};

export default OrderCheckoutModal;
